
import { BadgeAlert, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CorsErrorProps {
  onRetry?: () => void;
  errorMessage?: string;
}

export function CorsError({ onRetry, errorMessage }: CorsErrorProps) {
  // Check if there's a data mapping error
  const isDataMapError = errorMessage?.includes('data.map is not a function');

  return (
    <div className="mt-4 p-4 border border-red-700 rounded-md bg-red-900/20">
      <p className="flex items-center">
        <BadgeAlert className="h-4 w-4 mr-2" />
        <strong>
          {isDataMapError 
            ? "API Response Format Error" 
            : "Cross-Origin Request Blocked"}
        </strong>
      </p>
      
      <p className="mt-2 text-sm">
        {isDataMapError 
          ? "The API returned data in an unexpected format. This might be due to changes in the API response structure."
          : "Your browser is blocking direct access to the TikTok API due to security restrictions. This application uses a CORS proxy to access TikTok's API."}
      </p>
      
      <div className="mt-3 bg-black/20 p-3 rounded border border-red-800/50 text-sm">
        <p className="font-semibold mb-1">Possible Solutions:</p>
        <ul className="list-disc pl-5 space-y-1 text-xs">
          {isDataMapError ? (
            <>
              <li>Check if the TikTok URL is a valid collection URL</li>
              <li>The API may have changed its response format</li>
              <li>Try using a different collection URL</li>
            </>
          ) : (
            <>
              <li>Ensure your TikTok URL is correctly formatted</li>
              <li>Try a different network connection</li>
              <li>The TikTok API might be temporarily unavailable</li>
            </>
          )}
        </ul>
      </div>
      
      <div className="mt-3 flex flex-wrap gap-2">
        {onRetry && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={onRetry}
            className="bg-red-900/30 hover:bg-red-800/50 border-red-700"
          >
            <RefreshCw className="mr-1 h-3 w-3" />
            Try Again
          </Button>
        )}
        
        <Button 
          variant="link" 
          size="sm" 
          className="text-blue-400 hover:text-blue-300"
          onClick={() => window.open('https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS', '_blank')}
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          Learn About CORS
        </Button>
      </div>
    </div>
  );
}
